// The staff operator console (`SC-F903`), the reconciler's guards (`SC-F305`)
// and the orphan quarantine ladder (`02` §9.5).
//
// **Read this before changing a shape here.** `06` §3.16 deliberately puts
// `/backoffice/*` OUTSIDE the public contract: it is not in the OpenAPI
// document, it is "documented separately", and no such document exists — `06`
// §8.11 raises the staff principal as the blocking open decision it is. So
// unlike every other module in this directory, the types below are **derived,
// not transcribed**. Their sources, named per field so a reviewer can check
// them:
//
//   - `05` §3.8 `swarm_objects` — the observed-state collection, authoritative
//     for nothing, which is where quarantine is recorded.
//   - `02` §9.4 (drift classes), §9.5 (the quarantine ladder and `SC-F305`'s
//     four rules) and §9.7 (the three manual escape hatches).
//   - `SC-S902` for what the console must render, `SC-S306` / `SC-S307` for the
//     guard's observable behaviour, `SC-S908` for the step-up obligation on
//     every mutating staff action.
//
// When the backoffice document lands, this module is what it has to agree with.
//
// Two rules that are not negotiable and are visible in the shapes:
//
//   - **No secret values and no customer query results, ever** (`SC-S902`).
//     Nothing here returns policy content, a rendered config, a datasource
//     credential or an audit record. The console reads *state and shape*.
//   - **Every mutation takes a reason and a step-up grant.** `SC-S908` binds a
//     grant to its target, so a grant taken for one organization cannot be spent
//     on another, and `SC-F903` requires a recorded ticket reference on the one
//     membership-mutating action there is.

import { getJSON, newIdempotencyKey, query, sendJSON, type Page, type PageParams } from "./http.js";
import type { Instance, InstanceState, InstanceStatus } from "./instances.js";
import type { ChangeClass, GenerationState } from "./generations.js";

const BASE = "/backoffice";

// ───────────────────────────────────────────────────────── the fleet ──

/** FleetSummary is the one-glance header of the console. Counts only. */
export interface FleetSummary {
  organizations: number;
  instances: number;
  /** Keyed by `InstanceState`; a state with no instances may be absent. */
  instances_by_state: Partial<Record<InstanceState, number>>;
  /** Swarm services carrying our labels, the denominator of the blast-radius
   * fraction (`02` §9.5 rule 3). */
  managed_services: number;
  quarantined_objects: number;
  jobs_in_flight: number;
}

/** getFleetSummary reads the fleet counters. */
export function getFleetSummary(): Promise<FleetSummary> {
  return getJSON<FleetSummary>(`${BASE}/summary`);
}

/**
 * isStaffPrincipal reports whether the credential this browser holds is a staff
 * principal, so the app shell can decide whether to offer the console at all.
 *
 * **It is a probe, and it has to be.** `01` §0.1 stores staff in a separate
 * collection rather than as a flag on a customer record — "a staff principal has
 * no organization role" is structural, not conventional — so `GET /v1/me`, which
 * describes a *customer* credential, carries nothing to read. The only question
 * the API can answer is the one the middleware already answers on every
 * `/backoffice/*` route: it refuses all four customer credential classes by name
 * (`01` §0.1, `06` §3.16). Asking it once is therefore feature detection in the
 * `I4` sense — ask what this credential can do, never infer it from a shape.
 *
 * Every failure is `false`, including a transport failure: an affordance that
 * opens a console the browser cannot reach is worse than no affordance.
 *
 * The caller is expected to skip this entirely for anyone holding an
 * organization membership, which is every customer (`SC-F101` mints a personal
 * organization at sign-up), so an ordinary session never spends a request on a
 * question whose answer is structurally no.
 */
export async function isStaffPrincipal(): Promise<boolean> {
  try {
    await getFleetSummary();
    return true;
  } catch {
    return false;
  }
}

/** BackofficeOrg is one organization as an operator sees it: shape, not
 * content. No plan pricing, no billing identifiers, no member emails. */
export interface BackofficeOrg {
  id: string;
  slug: string;
  name: string;
  personal: boolean;
  member_count: number;
  owner_count: number;
  instance_count: number;
  created: string;
}

/** listBackofficeOrgs lists organizations across the whole fleet. `q` matches
 * the slug and the display name. */
export function listBackofficeOrgs(
  params: { q?: string } & PageParams = {},
): Promise<Page<BackofficeOrg>> {
  return getJSON<Page<BackofficeOrg>>(`${BASE}/orgs${query({ ...params })}`);
}

/**
 * BackofficeMember is a member of one organization, for the single membership
 * action the console has. `email` is present because the grant has to name a
 * human and an opaque id names nobody — it is the narrowest field set that lets
 * an operator pick the right person from a support ticket.
 */
export interface BackofficeMember {
  account: string;
  email: string;
  name: string;
  role: string;
  /** Last time this account authenticated at all, which is how an operator
   * corroborates "the sole owner is unreachable" (`SC-F107`). */
  last_seen: string | null;
}

/** listBackofficeOrgMembers reads one organization's membership. Staff cannot
 * call the customer route: it requires an organization role, and a staff
 * principal has none by construction (`SC-S908`). */
export function listBackofficeOrgMembers(orgId: string): Promise<Page<BackofficeMember>> {
  return getJSON<Page<BackofficeMember>>(`${BASE}/orgs/${encodeURIComponent(orgId)}/members`);
}

/**
 * grantOwner is the **only** membership-mutating action in the console
 * (`SC-F903`), and it exists because `SC-F103`'s last-owner invariant otherwise
 * strands an organization whose sole owner has become unreachable.
 *
 * Its five guardrails are enforced server-side and must be stated in the UI:
 * a distinct break-glass staff role, a recorded ticket reference, a
 * control-plane audit event visible to the organization's own members, mail to
 * every existing member, and — the one this signature encodes — it **grants**.
 * There is no demote, no remove, and `account` must already be a member.
 *
 * `verification` is a step-up grant whose target is this organization
 * (`SC-S908`): a grant taken for one organization cannot be spent on another.
 */
export function grantOwner(
  orgId: string,
  input: { account: string; ticket: string; reason: string },
  opts: { verification?: string } = {},
): Promise<{ account: string; role: "owner"; granted: string }> {
  return sendJSON(`${BASE}/orgs/${encodeURIComponent(orgId)}/grant-owner`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
    verification: opts.verification,
  });
}

// ────────────────────────────────────────────────────────── instances ──

/**
 * FleetInstance is one row of the cross-tenant instance list. It is the
 * customer-facing [Instance] plus the three things only an operator needs and
 * that `SC-S902` requires to be searchable: the organization it belongs to, the
 * Swarm service id, and the node its data is pinned to.
 */
export interface FleetInstance
  extends Pick<
    Instance,
    "id" | "org" | "name" | "slug" | "hostname" | "size" | "sluice_version" | "image_digest"
  > {
  org_slug: string;
  state: InstanceState;
  /** The live generation, or null before the first publish. */
  generation: number | null;
  /** What the cluster is actually running, which is the interesting column. */
  observed_generation: number | null;
  /** Empty before `provisioning` completes. */
  swarm_service_id: string;
  /** The pinned node (`02` §7.4). Empty when no task is placed. */
  node: string;
  /** `instances.reconcile_paused` — escape hatch 1 of `02` §9.7. */
  reconcile_paused: boolean;
  last_reconcile: string | null;
  created: string;
}

/** listFleetInstances lists instances across every organization. `q` matches
 * the instance name, the slug, the hostname and the Swarm service id — the four
 * handles `SC-S902` requires to be searchable. */
export function listFleetInstances(
  params: { q?: string; org?: string; state?: InstanceState } & PageParams = {},
): Promise<Page<FleetInstance>> {
  return getJSON<Page<FleetInstance>>(`${BASE}/instances${query({ ...params })}`);
}

/**
 * FleetGeneration is one publish-history row as the **operator** view spells it
 * (`backoffice.Generation`).
 *
 * It is deliberately NOT the customer-facing [Generation] from `./generations.js`,
 * and the difference is not cosmetic: this projection names the number `number`,
 * the rendered digest `bundle_digest`, the confirmed one `observed_digest` and the
 * timestamp `created`, and it folds a failed publish's reason into a nested
 * `error` envelope. Reusing the customer type here is how a console renders
 * `undefined` in four columns and a badge that never goes green — the state
 * vocabulary is shared, the field names are not.
 */
export interface FleetGeneration {
  /** Monotonic per instance (`config_generations.seq`). */
  number: number;
  /** The same six-value vocabulary the customer history uses: the live one is
   * `active`, never `live`. */
  state: GenerationState | string;
  message: string;
  /** What the control plane rendered. */
  bundle_digest: string;
  /** What the instance confirmed (`SC-F608`). Null until a publish confirms;
   * equality with `bundle_digest` **is** the success condition. */
  observed_digest: string | null;
  change_class: ChangeClass | string;
  created: string;
  created_by: string;
  /** The attribution snapshot, which survives the account (`05` §6.4). */
  created_by_label: string;
  /** Present on `publish_failed`, in the `06` §1.4 envelope shape minus
   * `request_id` — a job outlives the request that created it. */
  error: { code: string; message: string } | null;
}

/**
 * FleetInstanceDetail is what `SC-S902` requires an opened instance to show:
 * desired versus observed, the last reconcile outcome, the quarantine state, the
 * image digest and the recent publish history.
 *
 * `status` is the same [InstanceStatus] the customer console reads. It is reused
 * rather than re-shaped on purpose — an operator diagnosing a customer's problem
 * should be looking at the customer's numbers, not at a parallel set that can
 * disagree with them.
 */
export interface FleetInstanceDetail {
  instance: FleetInstance;
  status: InstanceStatus | null;
  /** Null when the orchestrator was unreachable for this read; the rest of the
   * record still renders (`SC-S317`). */
  status_error: { code: string; message: string } | null;
  reconcile: {
    last_pass: string | null;
    outcome: ReconcileOutcome | null;
    /** The drift class last repaired or refused, `D1`…`D12` (`02` §9.4). */
    drift_class: string | null;
    message: string;
    /** Consecutive failed repairs; six stops automatic repair (`02` §9.6). */
    consecutive_failures: number;
    paused: boolean;
    paused_reason: string;
  };
  /** Newest first, bounded server-side. Publish history only — no bundle
   * content, because that is customer configuration. */
  generations: FleetGeneration[];
}

/** getFleetInstance reads one instance's operator view. */
export function getFleetInstance(id: string): Promise<FleetInstanceDetail> {
  return getJSON<FleetInstanceDetail>(`${BASE}/instances/${encodeURIComponent(id)}`);
}

/**
 * setReconcilePaused is escape hatch 1 of `02` §9.7: the reconciler keeps
 * observing and reporting on this instance but performs no mutation. Used while
 * an operator is hand-editing. It surfaces as a banner on the instance so nobody
 * debugs a paused reconciler for an hour.
 */
export function setReconcilePaused(
  id: string,
  paused: boolean,
  input: { reason: string },
  opts: { verification?: string } = {},
): Promise<{ paused: boolean }> {
  return sendJSON(
    `${BASE}/instances/${encodeURIComponent(id)}/reconcile-paused`,
    "PUT",
    { paused, ...input },
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

// ───────────────────────────────────────────────────────── reconciler ──

/** ReconcileMode is escape hatch 2 of `02` §9.7. `observe` is fleet-wide
 * observe-only: the switch to throw when the reconciler is suspected of
 * misbehaving. */
export type ReconcileMode = "enforce" | "observe";

/** ReconcileOutcome is how a pass ended. `refused` is the blast-radius guard
 * declining the whole pass — not a failure, a control working. */
export type ReconcileOutcome = "converged" | "repaired" | "refused" | "failed";

/**
 * PlannedAction is one entry of a pass's intended plan. `SC-S307` requires the
 * refused plan to be visible **in full** — an operator cannot approve an
 * override they cannot read.
 */
export interface PlannedAction {
  action: "quarantine" | "remove_service" | "remove_object" | "reclaim_volume";
  object_kind: "service" | "config" | "secret" | "network" | "volume";
  swarm_id: string;
  swarm_name: string;
  /** From `io.sluicecloud.org` / `.instance`; these survive the database row,
   * which is exactly the case quarantine exists for (`05` §3.8). */
  org_label: string;
  instance_label: string;
  reason: string;
}

/**
 * BlastRadiusGuard is `SC-F305` rule 3 — the single rule that prevents the worst
 * available outage — expressed as something an operator can watch holding.
 *
 * `holding` is sticky by design: `SC-S307` requires that once the guard trips,
 * every subsequent pass also refuses until an operator overrides. A guard that
 * reset itself on the next tick would let a mass-delete through on the second
 * try.
 */
export interface BlastRadiusGuard {
  holding: boolean;
  /** `max(floor, fraction × managed_services)` is the threshold (`02` §9.5). */
  threshold_fraction: number;
  threshold_floor: number;
  managed_services: number;
  /** How many actions the refused pass wanted to take. */
  planned_action_count: number;
  refused_at: string | null;
  /** The intended plan, in full (`SC-S307`). Empty when the guard is not holding. */
  plan: PlannedAction[];
}

/**
 * ReconcileStatus is the reconciler as an operator needs to see it: the mode,
 * the last pass, and `SC-F305`'s four rules each with the number that shows it
 * working. The four counters are not decoration — a guard nobody can see is a
 * guard nobody trusts, and `SC-S903` makes the restore path depend on it.
 */
export interface ReconcileStatus {
  mode: ReconcileMode;
  last_pass: string | null;
  last_outcome: ReconcileOutcome | null;
  /** Which control-plane process holds the pass lease (`02` §9.1). */
  lease_holder: string | null;
  /** Instances with `reconcile_paused` set (escape hatch 1). */
  paused_instances: number;

  /** Rule 1 — never act on first observation. Passes an orphan must be seen on. */
  orphan_observation_threshold: number;
  /** Rule 1 — orphans seen but still below the threshold. No action taken. */
  candidate_orphans: number;
  /** Rule 2 — quarantine before delete. Scaled to zero, nothing removed. */
  quarantined_objects: number;
  /** Rule 3. */
  guard: BlastRadiusGuard;
  /** Rule 4 — refcounted, delayed config and secret GC. */
  pending_object_sweeps: number;
  object_sweep_delay_seconds: number;
}

/** getReconcileStatus reads the reconciler's mode, last pass and guards. */
export function getReconcileStatus(): Promise<ReconcileStatus> {
  return getJSON<ReconcileStatus>(`${BASE}/reconcile`);
}

/** setReconcileMode is escape hatch 2 of `02` §9.7: fleet-wide observe-only.
 * Recorded in the control-plane trail with actor, reason and duration. */
export function setReconcileMode(
  mode: ReconcileMode,
  input: { reason: string },
  opts: { verification?: string } = {},
): Promise<{ mode: ReconcileMode }> {
  return sendJSON(
    `${BASE}/reconcile/mode`,
    "PUT",
    { mode, ...input },
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

/**
 * acknowledgeBlastRadius is escape hatch 3 of `02` §9.7: it permits **one**
 * pass that exceeded the deletion threshold, with the operator's identity
 * recorded. `SC-S307` requires the override to name a reason, and the
 * acknowledgement is single-use — the guard re-arms on the next pass.
 */
export function acknowledgeBlastRadius(
  input: { reason: string },
  opts: { verification?: string } = {},
): Promise<{ acknowledged: string; expires: string }> {
  return sendJSON(`${BASE}/reconcile/blast-radius-ack`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
    verification: opts.verification,
  });
}

// ───────────────────────────────────────────────────────── quarantine ──

/**
 * QuarantinedObject is one row of `05` §3.8's `swarm_objects` that carries our
 * labels and has no database record. It is **not** an instance state
 * (`02` §2.1): by definition there is no instance record to put in a state.
 *
 * The labels are the whole value of the row. `05` §3.8 keeps `org_label` and
 * `instance_label` denormalized precisely so an orphan whose instance row is
 * gone can still be identified by a human.
 */
export interface QuarantinedObject {
  swarm_id: string;
  swarm_name: string;
  object_kind: "service" | "config" | "secret" | "network" | "volume";
  org_label: string;
  instance_label: string;
  generation_label: string;
  spec_hash: string;
  first_seen: string;
  last_seen: string;
  /** Consecutive passes this object has been observed as an orphan. */
  observations: number;
  /** Null while it is still a *candidate* below the observation threshold. */
  quarantined_at: string | null;
  quarantine_reason: string;
  /** An operator has acknowledged removal; `02` §9.5 removes 72 h after that. */
  acknowledged_at: string | null;
  acknowledged_by_label: string;
  /** When `ServiceRemove` becomes eligible. Null until acknowledged. */
  removable_at: string | null;
}

/** listQuarantined lists candidate and quarantined orphans, newest first.
 * `include_candidates` shows the rule-1 population that no action has been taken
 * on, which is what proves rule 1 is holding rather than merely configured. */
export function listQuarantined(
  params: { q?: string; include_candidates?: boolean } & PageParams = {},
): Promise<Page<QuarantinedObject>> {
  return getJSON<Page<QuarantinedObject>>(`${BASE}/quarantine${query({ ...params })}`);
}

/**
 * acknowledgeQuarantineRemoval marks a quarantined object for removal. It does
 * **not** remove anything: `02` §9.5 removes 72 h after acknowledgement, and the
 * volumes 30 days after that following an export. Quarantine is reversible and
 * removal is not, so the acknowledgement is a separate, recorded decision.
 */
export function acknowledgeQuarantineRemoval(
  swarmId: string,
  input: { reason: string },
  opts: { verification?: string } = {},
): Promise<QuarantinedObject> {
  return sendJSON<QuarantinedObject>(
    `${BASE}/quarantine/${encodeURIComponent(swarmId)}/acknowledge`,
    "POST",
    input,
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

/**
 * releaseQuarantine scales a quarantined service back to one replica and clears
 * the `quarantined-at` label. The reconciler already does this automatically
 * when the database row reappears (`SC-S306`); this is the manual path for an
 * operator who has repaired the record by hand and does not want to wait for a
 * pass.
 */
export function releaseQuarantine(
  swarmId: string,
  input: { reason: string },
  opts: { verification?: string } = {},
): Promise<QuarantinedObject> {
  return sendJSON<QuarantinedObject>(
    `${BASE}/quarantine/${encodeURIComponent(swarmId)}/release`,
    "POST",
    input,
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

// ─────────────────────────────── capacity and node inventory (`SC-F904`) ──

/** SizeRow is one of `02` §3.6's four resource classes.
 *
 * It is published beside the `fits` counts because "this pool fits 7 more medium
 * instances" means nothing to a reader who does not know a medium reserves 4 GiB.
 * Memory is reserved at 100 % of the limit — it is not burstable, and an
 * over-committed node kills tenants by OOM rather than slowing them down. */
export interface SizeRow {
  size: string;
  reserved_memory_bytes: number;
  reserved_nano_cpus: number;
  spill_volume_bytes: number;
  state_volume_bytes: number;
}

/** DiskAlarm is the state device's remaining headroom, named for consequence.
 *
 * `uninventoried` is **not** `full`: a zero device size means the operator never
 * labelled the device, and admission skips the disk dimension for such a node
 * rather than refusing every placement on it. "We did not measure" must never
 * render as "there is room". */
export type DiskAlarm = "none" | "uninventoried" | "tight" | "critical";

/** NodeRow is one node's headroom.
 *
 * `free_memory_bytes` can be **negative** on an over-committed node and must be
 * rendered as such rather than clamped: over-commitment on memory is the dimension
 * that kills. */
export interface NodeRow {
  id: string;
  pool: string;
  class: string;
  /** False for a drained, cordoned or unreachable node. It is rendered separately
   * from "has no room" because the remedy differs: a `NodeUpdate` for one,
   * hardware for the other. */
  available: boolean;
  free_memory_bytes: number;
  free_nano_cpus: number;
  advertisable_memory_bytes: number;
  advertisable_nano_cpus: number;
  free_spill_bytes: number;
  spill_device_bytes: number;
  free_state_bytes: number;
  state_device_bytes: number;
  state_alarm: DiskAlarm;
  state_alarm_detail?: string;
  /** How many more instances of each size class fit here, keyed by size name. */
  fits: Record<string, number>;
}

/** PoolRow is one tenancy tier's aggregate.
 *
 * `largest_free_memory_bytes` is the number that answers "what would fit", not
 * `free_memory_bytes`: a task runs on ONE node, so four nodes with 3 GiB free each
 * hold zero instances that need 4 GiB while the pool total reports three. */
export interface PoolRow {
  pool: string;
  /** True for `dedicated-<orgId>`, with the organization resolved. `SC-F310`'s
   * dedicated pool is the answer for a customer who needs hard isolation — Swarm
   * provides resource isolation and not security isolation — so which organization
   * owns a pool is the first thing an operator looking at one needs. */
  dedicated: boolean;
  org?: string;
  org_slug?: string;
  nodes: number;
  available_nodes: number;
  free_memory_bytes: number;
  free_nano_cpus: number;
  largest_free_memory_bytes: number;
  free_state_bytes: number;
  state_device_bytes: number;
  /** The WORST alarm any member carries, so a pool with one critical node never
   * reads as healthy. */
  state_alarm: DiskAlarm;
  critical_nodes: number;
  uninventoried_nodes: number;
  fits: Record<string, number>;
  members: NodeRow[];
}

/** CapacityView is `GET /backoffice/capacity` (`SC-F904`).
 *
 * **The disk column is a data-plane alarm, not a capacity one.** A Swarm service
 * spec has no disk field at all, so capacity is enforced outside the substrate by
 * a per-instance filesystem quota plus this ledger. A state device that fills does
 * not slow anything down: the state volume holds the hash-chained audit file,
 * `audit.failClosed` defaults to true, and every instance pinned to that node —
 * from every organization — serves zero rows with a 503.
 *
 * Everything here is a snapshot of RESERVATIONS and holds nothing. Two concurrent
 * creates can both be told a pool has room for one; the loser is refused by the
 * same admission check on its own attempt. Use it to plan, never to promise. */
export interface CapacityView {
  control_plane: string;
  pools: PoolRow[];
  nodes: number;
  available_nodes: number;
  sizes: SizeRow[];
  /** The dimensions the substrate cannot isolate at all (`02` §8.3). Destined
   * verbatim for the threat model and for customer-facing isolation claims, which
   * is why it names only the genuinely uncontrolled rows — a table that overstates
   * the gap is as harmful as one that understates it. */
  uncontrolled: string[];
  /** What these figures must not be used for. */
  caveats: string[];
  /** False when the ledger could not be read. An empty `pools` with this false and
   * an empty `pools` with it true mean opposite things — "we cannot see the nodes"
   * and "there are no nodes" — and only the second is an alarm. */
  link_available: boolean;
  link_error: { code: string; message: string } | null;
  observed_at: string;
}

/** getCapacity reads the fleet's pools and per-node headroom. */
export function getCapacity(): Promise<CapacityView> {
  return getJSON<CapacityView>(`${BASE}/capacity`);
}

// ───────────────────────────── support diagnostics bundle (`SC-F905`) ──

/** DiagnosticsSecret is one sealed secret's METADATA. There is no field for a
 * value, and there is no route that returns one: the inventory answers "is a
 * secret materialized for this datasource" without disclosing anything. */
export interface DiagnosticsSecret {
  purpose: string;
  name: string;
  datasource?: string;
  mount_path: string;
  /** Empty before materialization — which is itself the answer to the most common
   * support question about a failing datasource. */
  swarm_secret_name?: string;
  key_id: string;
  value_digest: string;
  rotated_from?: string;
  superseded_at?: string;
  created: string;
}

/** DiagnosticsObject is one managed Swarm object as observed state records it.
 * `spec_hash` and `generation_label` are the two labels `02` §9.4's D3 and D4 turn
 * on, so their presence is what lets an operator reproduce the reconciler's own
 * verdict from a file. */
export interface DiagnosticsObject {
  object_kind: string;
  swarm_id: string;
  swarm_name: string;
  spec_hash?: string;
  generation_label?: string;
  first_seen: string;
  last_seen: string;
  quarantined_at?: string;
  quarantine_reason?: string;
}

/** DiagnosticsEvent is one lifecycle record — OUR log of what we did to the
 * instance, never the instance's log of what its callers asked for. The second is
 * the audit trail and it is the customer's. */
export interface DiagnosticsEvent {
  type: string;
  severity: string;
  message: string;
  actor_label?: string;
  generation?: number;
  occurred_at: string;
}

/** DiagnosticsJob is one asynchronous job's outcome, with its error verbatim. */
export interface DiagnosticsJob {
  id: string;
  type: string;
  status: string;
  error?: string;
  created: string;
  updated: string;
}

/** DiagnosticsInstance is the instance's identity and desired state. */
export interface DiagnosticsInstance {
  id: string;
  org: string;
  org_slug: string;
  name: string;
  slug: string;
  hostname: string;
  state: string;
  desired_state: string;
  size: string;
  sluice_version: string;
  image_digest: string;
  audit_profile: string;
  node: string;
  replicas: number;
  reconcile_paused: boolean;
  reconcile_paused_reason?: string;
  created: string;
}

/** DiagnosticsBundle is `GET /backoffice/instances/{id}/diagnostics`
 * (`SC-F905`): one artifact an operator collects to debug a customer's instance
 * without reading their data.
 *
 * **It is defined by exclusion and the exclusions are structural.** No customer row
 * data — the control plane never carries result rows on the query path, so there is
 * no code path from this artifact to a row. No audit records and in particular no
 * `sql_sample`: the per-query trail is not read at all, not the bodies, not the
 * index, not a count. No secret values, only the inventory. No policy content and
 * no rendered configuration.
 *
 * `contains` and `omits` are the artifact's own manifest, and they are DATA rather
 * than documentation because the exclusions are the feature: a customer asking
 * "what did you take" is entitled to an answer from the file itself.
 *
 * **Every collection is recorded in both trails, uncoalesced.** Unlike the
 * console's other reads, every build writes a row — the granularity that suits a
 * polled page does not suit "support took a copy of your instance's state". */
export interface DiagnosticsBundle {
  kind: string;
  version: number;
  control_plane: string;
  collected_at: string;
  /** The staff actor, `staff:`-prefixed, so a bundle can never be mistaken for
   * something a customer generated. */
  collected_by: string;
  ticket?: string;
  instance: DiagnosticsInstance;
  /** The customer's own status projection, passed through untouched, or null with
   * `status_error` populated. */
  status: InstanceStatus | null;
  status_error: { code: string; message: string } | null;
  /** The publish history as METADATA: numbers, states, both digests, change
   * classes, attribution and failure reasons. No content. It is the same shape the
   * console's instance detail uses, deliberately — an operator reading a stored
   * artifact and an operator reading the live panel should be looking at the same
   * columns. */
  generations: FleetGeneration[];
  secrets: DiagnosticsSecret[];
  objects: DiagnosticsObject[];
  events: DiagnosticsEvent[];
  jobs: DiagnosticsJob[];
  contains: string[];
  omits: string[];
  /** Where the collection was recorded, so the file says the customer was told. */
  audited: string;
  /** Sections that hit their per-section cap, so a short list is never mistaken
   * for a complete one. */
  truncated?: string[];
}

/**
 * getDiagnostics collects the support bundle for one instance.
 *
 * `ticket` is optional here and required on the owner grant, and the asymmetry is
 * deliberate: a read that demanded a ticket number would be skipped during the
 * incident it exists for, and the trail row is written either way.
 */
export function getDiagnostics(
  instanceId: string,
  params: { ticket?: string } = {},
): Promise<DiagnosticsBundle> {
  return getJSON<DiagnosticsBundle>(
    `${BASE}/instances/${encodeURIComponent(instanceId)}/diagnostics${query(params)}`,
  );
}
